import { Box, Container, useMediaQuery, useTheme } from "@mui/material";
import { usePathname, useRouter } from "next/navigation";
import React, { useCallback, useEffect, useState } from "react";
import { useHydrated } from "src/hooks/use-hydrated";
import { HeaderNavInterface, SocialGroupInterface } from "src/lib/utils/interfaces";
import { SalifyHeaderLogoWrapper } from "./common/logo-wrapper";
import {
  HaiAnhDesktopHeaderNav,
  HaiAnhHeaderTopBar,
  HaiAnhResponsiveHeaderNav,
} from "./haianh/components";

interface VariantProps {
  logoUrl?: string;
  logoWidth?: number;
  logoHeight?: number;
  name: string | undefined;
  hotline: string | undefined;
  topBarInfoText: string | undefined;
  nav: Array<HeaderNavInterface> | undefined;
  socialGroup: Array<SocialGroupInterface> | undefined;
  socialSpacing?: number;
  showSearch?: boolean;
  searchPlaceholder?: string;
  nameStyle?: {
    fontSize?: string;
    fontWeight?: number;
    color?: string;
    fontFamily?: string;
  };
}

const HeaderHaiAnh: React.FC<VariantProps> = (props) => {
  const theme = useTheme();
  const hydrated = useHydrated();
  const isUnderLaptopMq = useMediaQuery(theme.breakpoints.down("laptop"));
  const isUnderLaptop = hydrated && isUnderLaptopMq;

  const [activeSection, setActiveSection] = useState<string | undefined>();
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    const timeout = setTimeout(() => {
      const params = new URLSearchParams(window.location.search);
      const targetId = params.get("scrollTo");

      if (targetId) {
        const el = document.getElementById(targetId);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }

      const url = new URL(window.location.href);
      url.searchParams.delete("scrollTo");
      window.history.replaceState({}, "", url.toString());
    }, 100);

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visibleSections = entries
          .filter((entry) => entry.isIntersecting)
          .map((entry) => entry.target.getAttribute("id"))
          .filter(Boolean) as string[];

        if (visibleSections.length > 0) {
          setActiveSection(visibleSections[0]);
        }
      },
      {
        rootMargin: "0px 0px -50% 0px",
        threshold: 0.2,
      },
    );

    const sectionElements = props.nav
      ?.filter((item) => item.action === "scroll" && item.target)
      .map((item) => document.getElementById(item.target!.replace("#", "")))
      .filter((el): el is HTMLElement => !!el);

    sectionElements?.forEach((el) => observer.observe(el));

    return () => {
      sectionElements?.forEach((el) => observer.unobserve(el));
    };
  }, [props.nav]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const sectionElements = props.nav
        ?.filter((item) => item.action === "scroll" && item.target)
        .map((item) => {
          const id = item.target!.replace("#", "");
          return document.getElementById(id);
        })
        .filter((el): el is HTMLElement => !!el);

      if (sectionElements && sectionElements.length > 0) {
        for (const el of sectionElements) {
          const rect = el.getBoundingClientRect();
          if (rect.top >= 0 && rect.top <= window.innerHeight / 2) {
            setActiveSection(el.id);
            break;
          }
        }
      }
    }, 100);

    return () => clearTimeout(timeout);
  }, [props.nav]);

  const isNavItemActive = useCallback(
    (item: HeaderNavInterface, sectionId?: string): boolean => {
      if (item.action === "scroll") {
        return activeSection === sectionId || (activeSection === undefined && item.target === "/");
      } else if (item.action === "link") {
        return pathname === item.target || (item.target === "/" && pathname === "/");
      }
      return pathname !== "/" && !!item.target && pathname.startsWith(`/${item.target}`);
    },
    [activeSection, pathname],
  );

  const onClickNavItem = (item: HeaderNavInterface, sectionId: string | undefined) => {
    if (item.action !== "scroll") {
      return;
    }

    // Handle scroll action
    if (item.target === "/" && pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    if (pathname !== "/") {
      router.push(`/?scrollTo=${sectionId}`);
    } else {
      const el = document.getElementById(sectionId ?? "");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  };

  return (
    <>
      <HaiAnhHeaderTopBar
        name={props.name}
        topBarInfoText={props.topBarInfoText}
        socialGroup={props.socialGroup}
        socialSpacing={props.socialSpacing}
        showSearch={false}
        searchPlaceholder={props.searchPlaceholder}
        nameStyle={props.nameStyle}
      />

      <Box
        bgcolor="background.paper"
        position="sticky"
        top={0}
        zIndex={3}
        sx={{
          borderBottom: "1px solid rgba(33, 33, 33, 0.08)",
        }}
      >
        <Container
          disableGutters
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingX: { laptop: 0, mobile: 2 },
            paddingY: { laptop: 0, mobile: 1 },
            minHeight: { laptop: "auto", mobile: "60px" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <SalifyHeaderLogoWrapper
              logoUrl={props.logoUrl}
              logoWidth={isUnderLaptop ? 100 : props.logoWidth}
              logoHeight={isUnderLaptop ? 32 : props.logoHeight}
            />
          </Box>

          {!!props?.nav?.length &&
            (!isUnderLaptop ? (
              <HaiAnhDesktopHeaderNav
                nav={props.nav}
                hotline={props.hotline}
                isActive={isNavItemActive}
                onClick={onClickNavItem}
                showSearch={props.showSearch}
                searchPlaceholder={props.searchPlaceholder}
              />
            ) : (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <HaiAnhResponsiveHeaderNav
                  nav={props.nav}
                  socialGroup={props.socialGroup}
                  isActive={isNavItemActive}
                  onClick={onClickNavItem}
                  hotline={props.hotline}
                />
              </Box>
            ))}
        </Container>
      </Box>
    </>
  );
};

HeaderHaiAnh.displayName = "HeaderHaiAnh";

export default HeaderHaiAnh;
